import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Select,
  MenuItem,
  Popover,
  IconButton,
  Divider as MuiDivider,
  Box,
  Tooltip
} from '@mui/material';
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list';
import { updateStyleProperty } from './utils';

// Constants
const colors = ['#000000', '#FFFFFF', '#FF0000', '#00B050', '#0070C0', '#FFC000', '#FF6600', '#7030A0', '#808080', '#FF00FF'];
const fontFamilies = ['Poppins', 'Times New Roman', 'Arial', 'Georgia', 'Courier New'];
const fontSizes = ['12', '14', '16', '18', '20', '24', '28', '32'];

function Divider() {
  return <MuiDivider orientation="vertical" flexItem sx={{ my: 0.5, mx: 0.5 }} />;
}

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right' | 'justify'>('left');
  const [fontSize, setFontSize] = useState<string>('16');
  const [fontFamily, setFontFamily] = useState<string>('Poppins');
  const [textColorAnchorEl, setTextColorAnchorEl] = useState<null | HTMLElement>(null);
  const textColorMenuOpen = Boolean(textColorAnchorEl);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor },
        );
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  const handleAlignment = (align: 'left' | 'center' | 'right' | 'justify') => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
    setAlignment(align);
  };

  const handleFontSizeChange = (e: any) => {
    const size = e.target.value;
    setFontSize(size);
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isTextNode(node)) {
            const style = node.getStyle();
            const newStyle = updateStyleProperty(style, 'font-size', `${size}px`);
            node.setStyle(newStyle);
          }
        });
      }
    });
  };

  const handleFontFamilyChange = (e: any) => {
    const family = e.target.value;
    setFontFamily(family);
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isTextNode(node)) {
            const style = node.getStyle();
            const newStyle = updateStyleProperty(style, 'font-family', family);
            node.setStyle(newStyle);
          }
        });
      }
    });
  };

  const handleTextColorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTextColorAnchorEl(event.currentTarget);
  };

  const handleTextColorClose = () => {
    setTextColorAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isTextNode(node)) {
            const style = node.getStyle();
            const newStyle = updateStyleProperty(style, 'color', color);
            node.setStyle(newStyle);
          }
        });
      }
    });
    handleTextColorClose();
  };

  return (
    <Box
      ref={toolbarRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
        flexWrap: 'wrap',
      }}
      className="toolbar"
    >
      {/* Undo/Redo */}
      <Tooltip title="Annuler (Ctrl+Z)">
        <span>
          <IconButton
            disabled={!canUndo}
            onClick={() => {
              editor.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            size="small"
            aria-label="Undo"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Refaire (Ctrl+Y)">
        <span>
          <IconButton
            disabled={!canRedo}
            onClick={() => {
              editor.dispatchCommand(REDO_COMMAND, undefined);
            }}
            size="small"
            aria-label="Redo"
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider />

      {/* Font Size */}
      <Tooltip title="Taille de police">
        <Select
          size="small"
          value={fontSize}
          onChange={handleFontSizeChange}
          sx={{ width: 60, height: 32 }}
        >
          {fontSizes.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>

      {/* Font Family */}
      <Tooltip title="Police">
        <Select
          size="small"
          value={fontFamily}
          onChange={handleFontFamilyChange}
          sx={{ width: 120, height: 32 }}
        >
          {fontFamilies.map((family) => (
            <MenuItem key={family} value={family} sx={{ fontFamily }}>
              {family}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>

      {/* Text Color */}
      <Tooltip title="Couleur du texte">
        <IconButton
          onClick={handleTextColorClick}
          size="small"
          aria-label="Text Color"
        >
          <FormatColorTextIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={textColorMenuOpen}
        anchorEl={textColorAnchorEl}
        onClose={handleTextColorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => handleColorSelect(color)}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
                border: '2px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { transform: 'scale(1.1)', borderColor: '#333' },
              }}
            />
          ))}
        </Box>
      </Popover>

      {/* Text Format */}
      <Tooltip title="Gras (Ctrl+B)">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
          size="small"
          color={isBold ? 'primary' : 'default'}
          aria-label="Format Bold"
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Italique (Ctrl+I)">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          size="small"
          color={isItalic ? 'primary' : 'default'}
          aria-label="Format Italics"
        >
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Souligné (Ctrl+U)">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          size="small"
          color={isUnderline ? 'primary' : 'default'}
          aria-label="Format Underline"
        >
          <FormatUnderlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Barré">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          }}
          size="small"
          color={isStrikethrough ? 'primary' : 'default'}
          aria-label="Format Strikethrough"
        >
          <FormatStrikethroughIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider />

      {/* Lists */}
      <Tooltip title="Liste à puces">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }}
          size="small"
          aria-label="Unordered List"
        >
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Liste numérotée">
        <IconButton
          onClick={() => {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }}
          size="small"
          aria-label="Ordered List"
        >
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider />

      {/* Alignment */}
      <Tooltip title="Aligner à gauche">
        <IconButton
          onClick={() => handleAlignment('left')}
          size="small"
          color={alignment === 'left' ? 'primary' : 'default'}
          aria-label="Left Align"
        >
          <FormatAlignLeftIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Centrer">
        <IconButton
          onClick={() => handleAlignment('center')}
          size="small"
          color={alignment === 'center' ? 'primary' : 'default'}
          aria-label="Center Align"
        >
          <FormatAlignCenterIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Aligner à droite">
        <IconButton
          onClick={() => handleAlignment('right')}
          size="small"
          color={alignment === 'right' ? 'primary' : 'default'}
          aria-label="Right Align"
        >
          <FormatAlignRightIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Justifier">
        <IconButton
          onClick={() => handleAlignment('justify')}
          size="small"
          color={alignment === 'justify' ? 'primary' : 'default'}
          aria-label="Justify Align"
        >
          <FormatAlignJustifyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider />

      {/* Generate with AI */}
      <Tooltip title="Générer par IA">
        <span>
          <IconButton
            disabled
            size="small"
            aria-label="Generate with AI"
          >
            <SmartToyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}